import { opportunityRepo } from "@repositories";
import { Box, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { communicationChannelLabel } from "@shared/helpers/communication-channel";
import { compareCallCenterTaskPriority } from "@shared/helpers/call-center-task-priority";
import { formatOpportunitySchedule } from "@shared/helpers/format-opportunity-schedule";
import { phoneToIsraelSignedPhone } from "@shared/helpers/phone-to-israel-signed-phone";
import { linko } from "@shared/linko";
import { OrganizationRoles } from "@shared/Roles";
import { useState, type FC, type FormEvent, type ReactNode } from "react";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { useAlert } from "../../contexts/AlertProvider";
import { useAddUserToOpportunity } from "../../queries/events.query";
import { CommunicationRecordsDialog } from "../../components/CommunicationRecordManager/CommunicationRecordsDialog";
import { useAssociationCommunicationRecordsDialog } from "../../components/CommunicationRecordManager/useAssociationCommunicationRecordsDialog";
import { CallCenterGroupAssign } from "./CallCenterGroupAssign";
import { CcButton, CcChip, CcEmpty, CcEyebrow, CcPanel, CcTrackChip } from "./CallCenterUi";
import { CC, ccFieldSx } from "./call-center-ui.theme";

type ScheduleFields = {
  id: string;
  title: string;
  location: string;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  hasTime: boolean;
  hasStartTime: boolean;
  timeDescription: string | null;
};

type CallerDossier = {
  userId: string;
  associationId: string;
  name: string;
  address: string | null;
  roles: string[];
  activityStatus: string;
  lastRelevantSmsAt: string | null;
  contacts: Array<{
    id: string;
    title: string;
    channel: string | null;
    createdAt: string;
    description: string;
  }>;
  aidRequests: ScheduleFields[];
  participations: Array<{
    id: string;
    status: string;
    groupName: string | null;
    opportunity: ScheduleFields;
  }>;
  groups: Array<{ id: string; name: string; role: "leader" | "member" }>;
};

type TaskRow = {
  id: string;
  title: string;
  location: string;
  scheduleLabel: string;
  distanceLabel: string | null;
  isGroup: boolean;
  defaultMatchWeight: number;
  callCenterUrgency: number;
  callCenterDifficulty: number;
};

const ROLE_LABEL: Record<string, string> = {
  [OrganizationRoles.Volunteer]: "מתנדב",
  [OrganizationRoles.Requesting]: "מבקש סיוע",
  [OrganizationRoles.InGroup]: "בקבוצה",
  [OrganizationRoles.InTeam]: "בצוות",
  [OrganizationRoles.InOpportunity]: "במשימה",
  [OrganizationRoles.Organization]: "ארגון",
};

const ACTIVITY_LABEL: Record<string, string> = {
  NEW: "חדש",
  ACTIVE: "פעיל",
  INACTIVE: "לא פעיל",
  LEGACY: "ותיק",
};

const REQUEST_STATUS_LABEL: Record<string, string> = {
  APPROVED: "אושרה",
  AID_REQUEST: "בקשת סיוע",
  DECLINED: "נדחתה",
};

const PARTICIPANT_STATUS_LABEL: Record<string, string> = {
  APPROVED: "אושר",
  PENDING: "ממתין",
  DECLINED: "סורב",
};

function formatWhen(value: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatDistance(km: number | null | undefined) {
  if (km == null || Number.isNaN(km)) return null;
  if (km < 1) return `${Math.round(km * 1000)} מ'`;
  return `${km.toFixed(1)} ק"מ`;
}

function scheduleLabel(row: ScheduleFields) {
  return formatOpportunitySchedule(row);
}

const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <CcPanel sx={{ p: 2 }}>
    <Stack gap={1.25}>
      <Typography sx={{ fontFamily: CC.font, fontWeight: 700, fontSize: 16, color: CC.ink }}>
        {title}
      </Typography>
      {children}
    </Stack>
  </CcPanel>
);

const CallCenterCallerExample: FC = () => (
  <Stack gap={1.5} sx={{ p: { xs: 1.5, md: 2 }, overflow: "auto" }}>
    <CcPanel data-tour="cc-caller-phone" sx={{ p: 2.25 }}>
      <Stack gap={1.25}>
        <CcEyebrow>הדגמה</CcEyebrow>
        <Typography sx={{ fontFamily: CC.font, fontWeight: 700, fontSize: 18, color: CC.ink }}>
          מיכל אזולאי · מתנדב
        </Typography>
        <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted }}>
          052-513-6682 · פתח תקווה. אדם לדוגמה, לא מי שנמצא עכשיו בשיחה.
        </Typography>
      </Stack>
    </CcPanel>
    <Typography sx={{ fontFamily: CC.font, fontWeight: 700, color: CC.ink }}>
      משימות לשיבוץ
    </Typography>
    <CcPanel sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
      <Stack sx={{ flex: 1, minWidth: 0 }} gap={0.6}>
        <Typography sx={{ fontFamily: CC.font, fontWeight: 700, fontSize: 16, color: CC.ink }}>
          ביקור בית לקשיש בפתח תקווה
        </Typography>
        <Stack direction="row" gap={0.75} flexWrap="wrap">
          <CcTrackChip isGroup={false} />
          <CcChip>פתח תקווה</CcChip>
        </Stack>
      </Stack>
      <CcButton data-tour="cc-caller-assign">שבץ</CcButton>
    </CcPanel>
  </Stack>
);

export const CallCenterCallerTasks: FC<{ example?: boolean }> = ({ example = false }) =>
  example ? <CallCenterCallerExample /> : <CallCenterCallerTasksLive />;

const CallCenterCallerTasksLive: FC = () => {
  const { currOrganization } = useOrganization();
  const { setAlertText, setSeverity } = useAlert();
  const { mutateAsync: addUser, isPending } = useAddUserToOpportunity();
  const [phone, setPhone] = useState("");
  const [caller, setCaller] = useState<CallerDossier | null>(null);
  const [missing, setMissing] = useState(false);
  const [rows, setRows] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [groupTaskId, setGroupTaskId] = useState<string | null>(null);

  const isVolunteer = caller?.roles.includes(OrganizationRoles.Volunteer) ?? false;

  const loadTasks = async (userId: string) => {
    if (!currOrganization?.id) return;
    const { data: ranked } =
      await linko.opportunity.queries.getRankedOpportunitiesForUser({
        userId,
        organizationId: currOrganization.id,
        limit: 15,
        radiusKm: 25,
      });
    const rankedList = (ranked ?? []) as Array<{
      id: string;
      distanceFromLocationKM?: number | null;
    }>;
    if (rankedList.length === 0) {
      setRows([]);
      return;
    }
    const opportunities = await opportunityRepo.find({
      where: { id: { $in: rankedList.map((row) => row.id) } },
      select: {
        id: true,
        title: true,
        location: true,
        startDate: true,
        endDate: true,
        hasTime: true,
        hasStartTime: true,
        timeDescription: true,
        isGroup: true,
        defaultMatchWeight: true,
        callCenterUrgency: true,
        callCenterDifficulty: true,
      },
    });
    const byId = new Map(opportunities.map((row) => [row.id, row]));
    setRows(
      rankedList
        .flatMap((row) => {
          const opportunity = byId.get(row.id);
          if (!opportunity) return [];
          return [
            {
              id: opportunity.id,
              title: opportunity.title,
              location: opportunity.location,
              scheduleLabel: formatOpportunitySchedule(opportunity),
              distanceLabel: formatDistance(row.distanceFromLocationKM),
              isGroup: opportunity.isGroup,
              defaultMatchWeight: opportunity.defaultMatchWeight ?? 1,
              callCenterUrgency: opportunity.callCenterUrgency ?? 0,
              callCenterDifficulty: opportunity.callCenterDifficulty ?? 0,
            },
          ];
        })
        .sort(compareCallCenterTaskPriority),
    );
  };

  const lookup = async (event?: FormEvent, options?: { quiet?: boolean }) => {
    event?.preventDefault();
    if (phone.length !== 10 || loading) return;
    if (!options?.quiet) {
      setLoading(true);
      setMissing(false);
      setCaller(null);
      setRows([]);
      setGroupTaskId(null);
    }
    try {
      const { data } = await linko.callCenter.queries.lookupCaller({
        phoneNumber: phoneToIsraelSignedPhone(phone) as `+972${number}`,
      });
      if (!data?.found) {
        setCaller(null);
        setRows([]);
        setMissing(true);
        return;
      }
      const dossier: CallerDossier = {
        userId: data.userId,
        associationId: data.associationId,
        name: data.name,
        address: data.address,
        roles: data.roles,
        activityStatus: data.activityStatus,
        lastRelevantSmsAt: data.lastRelevantSmsAt,
        contacts: data.contacts,
        aidRequests: data.aidRequests,
        participations: data.participations,
        groups: data.groups,
      };
      setCaller(dossier);
      setMissing(false);
      if (dossier.roles.includes(OrganizationRoles.Volunteer)) {
        await loadTasks(dossier.userId);
      } else {
        setRows([]);
      }
    } finally {
      if (!options?.quiet) setLoading(false);
    }
  };

  const { openCommunicationRecords, communicationRecordsDialogProps } =
    useAssociationCommunicationRecordsDialog({
      onSave: () => {
        void lookup(undefined, { quiet: true });
      },
    });

  const lastContact =
    formatWhen(caller?.contacts[0]?.createdAt ?? null) ??
    formatWhen(caller?.lastRelevantSmsAt ?? null);

  return (
    <Stack
      gap={2}
      sx={{ p: { xs: 1.5, md: 2.5 }, overflow: "auto", flex: 1 }}
    >
      <Stack gap={0.75}>
        <CcEyebrow>אדם בשיחה</CcEyebrow>
        <Typography
          sx={{
            fontFamily: CC.font,
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: "-0.03em",
            color: CC.ink,
          }}
        >
          מתנדב או מבקש סיוע שכבר על הקו
        </Typography>
        <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted, lineHeight: 1.6 }}>
          הזינו טלפון. מוצגים קשר קודם, בקשות, שיבוצים, וקבוצות. למתנדב גם משימות לשיבוץ.
        </Typography>
      </Stack>

      <CcPanel data-tour="cc-caller-phone" sx={{ p: 2.25 }}>
        <Stack
          component="form"
          direction={{ xs: "column", md: "row" }}
          gap={1.25}
          alignItems={{ md: "center" }}
          onSubmit={(event) => void lookup(event)}
        >
          <TextField
            label="טלפון"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            inputProps={{ maxLength: 10, dir: "ltr", inputMode: "numeric" }}
            sx={{ ...ccFieldSx, minWidth: 220, flex: 1 }}
            autoFocus
          />
          <CcButton type="submit" disabled={phone.length !== 10 || loading}>
            {loading ? "מחפשים..." : "חיפוש"}
          </CcButton>
        </Stack>
      </CcPanel>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : null}

      {missing ? (
        <CcPanel sx={{ p: 2.5 }}>
          <Stack gap={1.5}>
            <Typography sx={{ fontFamily: CC.font, fontWeight: 700, color: CC.ink }}>
              המספר לא נמצא בארגון
            </Typography>
            <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted }}>
              אפשר לשלוח קישור כללי, או לעבור לחיפוש לפי אזור.
            </Typography>
            <Box>
              <CcButton
                disabled={phone.length !== 10}
                onClick={async () => {
                  try {
                    await linko.callCenter.mutations.sendFeedLinkSms({
                      phoneNumber: phone as `+972${number}`,
                    });
                    setSeverity("success");
                    setAlertText("נשלח קישור כללי");
                  } catch {
                    setSeverity("error");
                    setAlertText("לא ניתן לשלוח קישור. בדקו שקישור הפיד מוגדר לארגון");
                  }
                }}
              >
                שליחת קישור למערכת
              </CcButton>
            </Box>
          </Stack>
        </CcPanel>
      ) : null}

      {caller ? (
        <Stack gap={1.25}>
          <CcPanel sx={{ p: 2.25 }}>
            <Stack gap={1}>
              <Typography sx={{ fontFamily: CC.font, fontWeight: 700, fontSize: 22, color: CC.ink }}>
                {caller.name}
              </Typography>
              <Stack direction="row" gap={0.75} flexWrap="wrap">
                {caller.roles.map((role) => (
                  <CcChip key={role} tone={role === OrganizationRoles.Requesting ? "gold" : "ink"}>
                    {ROLE_LABEL[role] ?? role}
                  </CcChip>
                ))}
                <CcChip>{ACTIVITY_LABEL[caller.activityStatus] ?? caller.activityStatus}</CcChip>
              </Stack>
              {caller.address ? (
                <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted }}>
                  {caller.address}
                </Typography>
              ) : null}
            </Stack>
          </CcPanel>

          <Section title="יצרו איתו קשר">
            <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted }}>
              {lastContact ? `קשר אחרון: ${lastContact}` : "אין עדיין רשומת קשר"}
            </Typography>
            <Stack gap={1}>
              {caller.contacts.map((contact) => {
                const channel = communicationChannelLabel(contact.channel);
                const when = formatWhen(contact.createdAt);
                return (
                  <Stack key={contact.id} gap={0.25}>
                    <Typography sx={{ fontFamily: CC.font, fontWeight: 600, color: CC.ink }}>
                      {contact.title}
                    </Typography>
                    <Typography sx={{ fontFamily: CC.font, fontSize: 13, color: CC.inkMuted }}>
                      {[when, channel].filter(Boolean).join(" · ")}
                    </Typography>
                    {contact.description ? (
                      <Typography sx={{ fontFamily: CC.font, fontSize: 14, color: CC.inkSoft }}>
                        {contact.description}
                      </Typography>
                    ) : null}
                  </Stack>
                );
              })}
            </Stack>
            <Box>
              <CcButton
                kind="ghost"
                onClick={() =>
                  openCommunicationRecords({
                    associationId: caller.associationId,
                    name: caller.name,
                  })
                }
              >
                כל התקשורת
              </CcButton>
            </Box>
          </Section>

          {caller.aidRequests.length > 0 ? (
            <Section title="בקשות סיוע">
              {caller.aidRequests.map((request) => (
                <Stack key={request.id} gap={0.4}>
                  <Typography sx={{ fontFamily: CC.font, fontWeight: 600, color: CC.ink }}>
                    {request.title}
                  </Typography>
                  <Stack direction="row" gap={0.75} flexWrap="wrap">
                    {request.status ? (
                      <CcChip>{REQUEST_STATUS_LABEL[request.status] ?? request.status}</CcChip>
                    ) : null}
                    {request.location ? <CcChip>{request.location}</CcChip> : null}
                    <CcChip>{scheduleLabel(request)}</CcChip>
                  </Stack>
                </Stack>
              ))}
            </Section>
          ) : null}

          {caller.participations.length > 0 ? (
            <Section title="שיבוצים">
              {caller.participations.map((row) => (
                <Stack key={row.id} gap={0.4}>
                  <Typography sx={{ fontFamily: CC.font, fontWeight: 600, color: CC.ink }}>
                    {row.opportunity.title}
                  </Typography>
                  <Stack direction="row" gap={0.75} flexWrap="wrap">
                    <CcChip>{PARTICIPANT_STATUS_LABEL[row.status] ?? row.status}</CcChip>
                    {row.groupName ? <CcChip>{row.groupName}</CcChip> : null}
                    {row.opportunity.location ? <CcChip>{row.opportunity.location}</CcChip> : null}
                    <CcChip>{scheduleLabel(row.opportunity)}</CcChip>
                  </Stack>
                </Stack>
              ))}
            </Section>
          ) : null}

          {caller.groups.length > 0 ? (
            <Section title="קבוצות">
              <Stack direction="row" gap={0.75} flexWrap="wrap">
                {caller.groups.map((group) => (
                  <CcChip key={group.id} tone={group.role === "leader" ? "gold" : "ink"}>
                    {group.name}
                    {group.role === "leader" ? " · ראש קבוצה" : " · מתנדב בקבוצה"}
                  </CcChip>
                ))}
              </Stack>
            </Section>
          ) : null}
        </Stack>
      ) : null}

      {!loading && isVolunteer && rows.length === 0 ? (
        <CcEmpty
          title="אין משימות פתוחות שמתאימות לו"
          body="נסו חיפוש כללי לפי אזור, או שלחו קישור לפיד."
        />
      ) : null}

      {isVolunteer ? (
        <Stack gap={1.25}>
          {rows.length > 0 ? (
            <Typography sx={{ fontFamily: CC.font, fontWeight: 700, color: CC.ink }}>
              משימות לשיבוץ
            </Typography>
          ) : null}
          {rows.map((row) => (
            <CcPanel key={row.id} sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <Stack sx={{ flex: 1, minWidth: 0 }} gap={0.6}>
                <Typography sx={{ fontFamily: CC.font, fontWeight: 700, fontSize: 16, color: CC.ink }}>
                  {row.title}
                </Typography>
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  <CcTrackChip isGroup={row.isGroup} />
                  <CcChip tone="gold">{`דחיפות ${row.callCenterUrgency}`}</CcChip>
                  <CcChip>{`קושי ${row.callCenterDifficulty}`}</CcChip>
                  {row.location ? <CcChip>{row.location}</CcChip> : null}
                  {row.distanceLabel ? <CcChip>{row.distanceLabel}</CcChip> : null}
                  {row.scheduleLabel ? <CcChip>{row.scheduleLabel}</CcChip> : null}
                </Stack>
              </Stack>
              <CcButton
                disabled={!row.isGroup && (isPending || !caller?.associationId)}
                onClick={async () => {
                  if (row.isGroup) {
                    setGroupTaskId(row.id);
                    return;
                  }
                  if (!caller?.associationId) return;
                  await addUser({
                    opportunityId: row.id,
                    associationId: caller.associationId,
                    matchWeight: row.defaultMatchWeight ?? 1,
                  });
                  await lookup(undefined, { quiet: true });
                }}
              >
                {row.isGroup ? "שבץ קבוצה" : "שבץ"}
              </CcButton>
            </CcPanel>
          ))}
          {groupTaskId ? (
            <CcPanel sx={{ p: 0, overflow: "hidden", minHeight: 280 }}>
              <CallCenterGroupAssign
                opportunityId={groupTaskId}
                matchWeight={
                  rows.find((row) => row.id === groupTaskId)?.defaultMatchWeight ?? 1
                }
              />
            </CcPanel>
          ) : null}
        </Stack>
      ) : null}

      {communicationRecordsDialogProps.open ? (
        <CommunicationRecordsDialog {...communicationRecordsDialogProps} />
      ) : null}
    </Stack>
  );
};
